import Banner from '@/components/UI/Banner';
import Chip from '@/components/UI/Chip/Chip';
import { _isNotEmpty, _notNil } from '@/littledash';
import type {
  CrossDomainFinding,
  CrossDomainFindingGroup,
  FindingsSummary,
  Severity,
  Truncation,
} from '@/model/Agent.model';
import { useMemo, type FC } from 'react';
import styles from './sendResults.module.scss';

interface SendCrossDomainResultsProps {
  groups: Array<CrossDomainFindingGroup>;
  summary: FindingsSummary;
  truncation?: Array<Truncation> | null;
  testId?: string;
}

const severityRank: Record<Severity, number> = { error: 0, warning: 1, info: 2 };

const humanize = (value: string): string => value.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());

/** The label for a domain-set group, e.g. `DM ↔ LB` or `DM ↔ SE ↔ TA`. */
const groupLabel = (domains: Array<string>): string => domains.join(' ↔ ');

/** A React key for a finding within a group; findings carry no id, so combine identifying fields. */
const findingKey = (finding: CrossDomainFinding, index: number): string =>
  `${finding.category}-${finding.key_variable ?? ''}-${index}`;

/**
 * Renders the cross-domain tier of a bundle validation: findings grouped by the sorted set of domains
 * each check spanned (`DM ↔ LB`), each with an origin badge separating a deterministic verdict from an
 * advisory AI observation.
 */
const SendCrossDomainResults: FC<SendCrossDomainResultsProps> = ({
  groups,
  summary,
  truncation,
  testId = 'invivo-send-validator__cross-domain',
}) => {
  const sortedGroups = useMemo(
    () =>
      [...groups].map((group) => ({
        ...group,
        findings: [...group.findings].sort((a, b) => severityRank[a.severity] - severityRank[b.severity]),
      })),
    [groups]
  );

  const blocking = summary.submission_blocking_count;
  const totalCapped = _notNil(truncation) ? truncation.reduce((acc, item) => acc + item.omitted_count, 0) : 0;

  return (
    <div
      className="ui-card pa4"
      data-test-component="SendCrossDomainResults"
      data-test-element="container"
      data-testid={testId}
    >
      <h2 className="f4 fw5 ma0 mb3" data-testid="invivo-send-validator__cross-domain-title">
        Cross-domain checks
      </h2>

      {_isNotEmpty(sortedGroups) ? (
        <Banner
          critical={blocking > 0}
          warning={blocking === 0}
          dismiss={false}
          testId="invivo-send-validator__cross-domain-banner"
        >
          <p className="ma0 fw5" data-testid="invivo-send-validator__cross-domain-headline">
            {sortedGroups.length} cross-domain {sortedGroups.length === 1 ? 'relationship' : 'relationships'} with
            findings
          </p>
        </Banner>
      ) : (
        <Banner success dismiss={false} testId="invivo-send-validator__cross-domain-banner">
          <p className="ma0 fw5" data-testid="invivo-send-validator__cross-domain-headline">
            No cross-domain issues found across the bundle
          </p>
        </Banner>
      )}

      {_notNil(truncation) && _isNotEmpty(truncation) && (
        <div className="mt3 f6 dark-gray" data-testid="invivo-send-validator__cross-domain-truncation">
          Some cross-domain categories were capped — {totalCapped} findings omitted
          {truncation.map((item) => (
            <span key={item.category} data-test-element="truncation-item" data-test-key={item.category}>
              {' '}
              ({humanize(item.category)}: {item.omitted_count} omitted)
            </span>
          ))}
        </div>
      )}

      {sortedGroups.map((group) => (
        <div
          key={groupLabel(group.domains)}
          className="mt3"
          data-test-element="cross-domain-group"
          data-test-key={groupLabel(group.domains)}
          data-testid="invivo-send-validator__cross-domain-group"
        >
          <h3 className="f5 fw5 ma0 mb2" data-test-element="cross-domain-group-label">
            {groupLabel(group.domains)}
          </h3>
          <ul className={styles.issueList}>
            {group.findings.map((finding, index) => (
              <li
                key={findingKey(finding, index)}
                className={styles.issueItem}
                data-test-element="cross-domain-finding"
              >
                <span className={`${styles.severityDot} ${styles[finding.severity]}`} aria-hidden="true" />
                <div className="flex flex-column">
                  <span>
                    <span className={`${styles.severityBadge} ${styles[finding.severity]} mr2`}>
                      {finding.severity}
                    </span>
                    {finding.message}
                  </span>
                  <span className="f6 dark-gray mt1" data-test-element="cross-domain-finding-meta">
                    {humanize(finding.category)}
                    {_notNil(finding.key_variable) ? ` · ${finding.key_variable}` : ''}
                    {_notNil(finding.occurrence_count) ? ` · ${finding.occurrence_count} occurrences` : ''}
                    {_notNil(finding.origin) ? (
                      <span
                        className="ml2"
                        data-test-element="cross-domain-finding-origin"
                        data-test-key={finding.origin}
                      >
                        <Chip
                          title={finding.origin === 'llm' ? 'AI' : 'Deterministic'}
                          colour={finding.origin === 'llm' ? 'gray' : 'blue'}
                        />
                      </span>
                    ) : null}
                  </span>
                  {_notNil(finding.sample_values) && _isNotEmpty(finding.sample_values) && (
                    <span className="f6 dark-gray mt1" data-test-element="cross-domain-finding-samples">
                      Examples: {finding.sample_values.join(', ')}
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
};

export default SendCrossDomainResults;
